/**
 * W4 — Object↔Flow binding contract
 */

import { ManifestEngine } from '@shared/manifest/ManifestEngine';
import type { UniversalBaseEntity } from '@shared/types/entity';
import { getRelatedFlows, type RelatedFlowRef } from './business-object-contract';
import { registerWorkbenchActions } from './action-registry';
import type { WorkbenchAction, ResolveActionsInput } from './action-types';
import { resolveActions } from './action-resolver';

export interface FlowBindingContext {
  objectId: string;
  businessDomain: string;
  tenantId: string;
}

export interface StartFlowBinding {
  flowTemplateId: string;
  label: string;
  prefillFromObject?: boolean;
}

export interface OpenFlowBinding extends RelatedFlowRef {
  executionId?: string;
  href?: string;
}

/**
 * SSOT — mirrors DynamicTable WORKFLOW_REGISTRATION (M4 manifest workflow ids).
 */
const WORKFLOW_START_TEMPLATES = {
  FinanceAccountingWorkflow: {
    flowTemplateId: 'finance.accounting',
    label: '회계 워크플로우',
    prefillFromObject: true,
  },
  EntityPermissionWorkflow: {
    flowTemplateId: 'entity.permission',
    label: '권한 워크플로우',
    prefillFromObject: true,
  },
  ItemInventoryWorkflow: {
    flowTemplateId: 'item.inventory',
    label: '재고 워크플로우',
    prefillFromObject: true,
  },
  FlowExecutionWorkflow: {
    flowTemplateId: 'flow.execute',
    label: 'Flow 실행',
    prefillFromObject: false,
  },
  OrderIntakeWorkflow: {
    flowTemplateId: 'order_intake',
    label: '주문 워크플로우',
    prefillFromObject: true,
  },
} as const;

type WorkflowManifestId = keyof typeof WORKFLOW_START_TEMPLATES;

const LEGACY_DOMAIN_WORKFLOW_MATCHERS: ReadonlyArray<{
  matches: (domain: string) => boolean;
  manifestId: WorkflowManifestId;
}> = [
  {
    matches: (domain) => domain.startsWith('finance'),
    manifestId: 'FinanceAccountingWorkflow',
  },
  {
    matches: (domain) => domain.startsWith('entity'),
    manifestId: 'EntityPermissionWorkflow',
  },
  {
    matches: (domain) => domain === 'item' || domain.startsWith('item') || domain.endsWith('_item'),
    manifestId: 'ItemInventoryWorkflow',
  },
  {
    matches: (domain) => domain.startsWith('warehouse') || domain.startsWith('inventory'),
    manifestId: 'ItemInventoryWorkflow',
  },
  {
    matches: (domain) => domain.startsWith('flow'),
    manifestId: 'FlowExecutionWorkflow',
  },
  {
    matches: (domain) =>
      domain === 'customer' ||
      domain === 'counterparty' ||
      domain === 'order' ||
      domain === 'sales_order',
    manifestId: 'OrderIntakeWorkflow',
  },
  {
    matches: (domain) => domain === 'supplier',
    manifestId: 'ItemInventoryWorkflow',
  },
  {
    matches: (domain) => domain === 'collection' || domain === 'ar_collection',
    manifestId: 'FinanceAccountingWorkflow',
  },
];

const DEFAULT_START_FLOW_BINDING: StartFlowBinding = {
  flowTemplateId: 'generic_intake',
  label: 'Flow 시작',
  prefillFromObject: true,
};

function templateFromManifestId(manifestId: string): StartFlowBinding | null {
  const template = WORKFLOW_START_TEMPLATES[manifestId as WorkflowManifestId];
  if (!template) {
    return null;
  }
  return { ...template };
}

function dedupeStartBindings(bindings: StartFlowBinding[]): StartFlowBinding[] {
  const seen = new Set<string>();
  const result: StartFlowBinding[] = [];
  for (const binding of bindings) {
    if (seen.has(binding.flowTemplateId)) {
      continue;
    }
    seen.add(binding.flowTemplateId);
    result.push(binding);
  }
  return result;
}

export function getStartFlowBindings(entity: UniversalBaseEntity): StartFlowBinding[] {
  const domain = entity.businessDomain ?? 'entity';

  if (ManifestEngine.hasManifest(domain)) {
    const fromManifest = dedupeStartBindings(
      ManifestEngine.getWorkflows(domain)
        .map(templateFromManifestId)
        .filter((binding): binding is StartFlowBinding => binding !== null)
    );
    if (fromManifest.length > 0) {
      return fromManifest;
    }
  }

  const fromLegacy = dedupeStartBindings(
    LEGACY_DOMAIN_WORKFLOW_MATCHERS.filter((entry) => entry.matches(domain))
      .map((entry) => templateFromManifestId(entry.manifestId))
      .filter((binding): binding is StartFlowBinding => binding !== null)
  );
  if (fromLegacy.length > 0) {
    return fromLegacy;
  }

  return [DEFAULT_START_FLOW_BINDING];
}

export function getOpenFlowBindings(entity: UniversalBaseEntity): OpenFlowBinding[] {
  return getRelatedFlows(entity)
    .filter((flow) => flow.status !== 'completed' && flow.status !== 'cancelled')
    .map((flow) => ({
      ...flow,
      ...(flow.flowId ? { href: `/workspace/operations/${flow.flowId}` } : {}),
    }));
}

export function buildStartFlowActions(
  bindings: readonly StartFlowBinding[],
  basePriority = 70
): WorkbenchAction[] {
  return bindings.map((binding, index) => ({
    id: `start-flow:${binding.flowTemplateId}`,
    label: binding.label,
    priority: basePriority - index,
    placement: 'secondary' as const,
    surfaces: ['table-row', 'detail-header', 'inspector', 'command'] as const,
    handlerRef: `flow:start:${binding.flowTemplateId}`,
    permissionKey: 'flow:execute',
  }));
}

export function resolveFlowBindingContext(
  entity: UniversalBaseEntity,
  tenantId: string
): FlowBindingContext {
  return {
    objectId: entity.id,
    businessDomain: entity.businessDomain ?? 'entity',
    tenantId,
  };
}

export function resolveStartFlowActions(
  entity: UniversalBaseEntity,
  input: ResolveActionsInput
): WorkbenchAction[] {
  registerWorkbenchActions(buildStartFlowActions(getStartFlowBindings(entity)));
  return resolveActions(entity, input).filter((action) =>
    action.handlerRef.startsWith('flow:start:')
  );
}
